import { Box, Container, Button, useBreakpointValue, Text, Td, Tr, Flex, useToast, Icon } from '@chakra-ui/react'
import dayjs from 'dayjs'
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { MdMeetingRoom, MdAccessTime } from 'react-icons/md'
import { AppLoading } from '../../components/common/AppLoading'
import { useRoomReservationListQuery } from '../../hooks/coreApi/useRoomReservations'
import { useRooms } from '../../hooks/coreApi/useRooms'
import { getRoomShortName } from '../../utils/room'
import { useReservation } from './ReservationContext'
import TimeSlotButton from './TimeSlotButton'
import TimeTable from './TimeTable'

// カラー定数
const PRIMARY_COLOR = '#6AA43B'

// 時間スロットの型（1列表示用に変更）
type TimeSlot = {
  hour: number;
  minute: number;
  timeString: string; // "09:00"形式
  available: boolean;
  selected: boolean;
}

const ReservationCreateRoomTime: React.FC = () => {
  const toast = useToast()
  const { state, goToNext, goBack } = useReservation()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [selectedRoom, setSelectedRoom] = useState<number | null>(state.roomId)
  
  // 選択された時間枠（複数の30分枠）
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([])
  
  const roomListQuery = useRooms({ isPasswordProtected: true })
  const roomReservationListQuery = useRoomReservationListQuery({})
  
  const now = dayjs()
  
  // 選択された日付
  const selectedDate = useMemo(() => {
    if (!state.date) return null
    return dayjs(state.date)
  }, [state.date])

  // 予約済みの時間帯を確認する関数
  const isTimeSlotReserved = useCallback((roomId: number, timeString: string): boolean => {
    if (!selectedDate) return true // 日付が選択されていない場合は予約不可とする
    
    const [hourStr, minuteStr] = timeString.split(':')
    const hour = parseInt(hourStr, 10)
    const minute = parseInt(minuteStr, 10)
    
    if (Number.isNaN(hour) || Number.isNaN(minute)) return true
    
    const slotTime = selectedDate.hour(hour).minute(minute)
    
    // 過去の時間は選択不可（現在時刻より前の時間）
    if (selectedDate.isSame(now, 'day') && slotTime.isBefore(now)) {
      return true
    }
    
    // 予約リストをチェック
    const roomReservations = roomReservationListQuery.data?.roomReservations ?? []
    return roomReservations.some(reservation => {
      if (!reservation) return false
      // ルームIDが一致し、時間が重複している場合は予約済み
      if (reservation.roomId !== roomId) return false
      
      const reservationStart = dayjs(reservation.startTime)
      const reservationEnd = dayjs(reservation.endTime)
      
      if (!reservationStart.isValid() || !reservationEnd.isValid()) return false
      
      // 同じ日付で、時間が重複しているかチェック
      return (
        selectedDate.isSame(reservationStart, 'day') &&
        (slotTime.isAfter(reservationStart) && slotTime.isBefore(reservationEnd) ||
         slotTime.isSame(reservationStart))
      )
    })
  }, [selectedDate, now, roomReservationListQuery.data?.roomReservations])

  // 初期表示時にstartTimeがある場合は、それに基づいて選択状態を設定
  useEffect(() => {
    if (state.startTime && state.endTime) {
      const startTime = dayjs(`${selectedDate?.format('YYYY-MM-DD')} ${state.startTime}`)
      const endTime = dayjs(`${selectedDate?.format('YYYY-MM-DD')} ${state.endTime}`)
      
      if (startTime.isValid() && endTime.isValid()) {
        const slots: string[] = []
        let current = startTime
        
        while (current.isBefore(endTime)) {
          slots.push(current.format('HH:mm'))
          current = current.add(30, 'minute')
        }
        
        setSelectedTimeSlots(slots)
      }
    }
  }, [state.startTime, state.endTime, selectedDate])
  
  // ルームが読み込まれたら、初期値として最初のルームを選択
  useEffect(() => {
    const rooms = roomListQuery.data?.rooms ?? []
    if (rooms.length > 0 && !selectedRoom) {
      setSelectedRoom(rooms[0].id)
    }
  }, [roomListQuery.data, selectedRoom])

  // 時間が選択されているかチェック
  const isTimeSlotSelected = useCallback((timeString: string): boolean => selectedTimeSlots.includes(timeString), [selectedTimeSlots])

  // 2つの時間文字列の間の分数を計算
  const getMinutesBetween = useCallback((time1: string, time2: string): number => {
    const [hour1, minute1] = time1.split(':').map(Number)
    const [hour2, minute2] = time2.split(':').map(Number)
    
    const totalMinutes1 = hour1 * 60 + minute1
    const totalMinutes2 = hour2 * 60 + minute2
    
    return Math.abs(totalMinutes2 - totalMinutes1)
  }, [])

  // 時間スロットを選択する処理
  const handleTimeSelect = (timeString: string): void => {
    if (!selectedRoom) {
      toast({
        title: '部屋を選択してください',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    
    if (isTimeSlotReserved(selectedRoom, timeString)) {
      toast({
        title: 'この時間は予約できません',
        description: 'すでに予約されているか、過去の時間です',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    
    // 1回目の選択の場合
    if (selectedTimeSlots.length === 0) {
      setSelectedTimeSlots([timeString])
      return
    }
    
    // 2回目以降の選択の場合
    const firstSelectedTime = selectedTimeSlots[0]
    const minuteDiff = getMinutesBetween(firstSelectedTime, timeString)
    
    // 1回目から30分〜60分の範囲内の場合、範囲選択（最大90分=3枠まで）
    if (minuteDiff >= 30 && minuteDiff <= 60) {
      const startTime = [firstSelectedTime, timeString].sort()[0]
      const endTime = [firstSelectedTime, timeString].sort()[1]
      
      // 開始時間から終了時間までの全ての30分枠を生成
      const slots: string[] = []
      const start = dayjs(`${selectedDate?.format('YYYY-MM-DD')} ${startTime}`)
      const end = dayjs(`${selectedDate?.format('YYYY-MM-DD')} ${endTime}`)
      let current = start
      
      while (current.isSameOrBefore(end)) {
        const slot = current.format('HH:mm')
        // 予約可能な枠のみ追加
        if (!isTimeSlotReserved(selectedRoom, slot)) {
          slots.push(slot)
        } else {
          // 途中に予約済みの枠がある場合はエラー
          toast({
            title: '選択範囲内に予約済みの時間があります',
            description: '別の時間を選択してください',
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
          return
        }
        current = current.add(30, 'minute')
      }
      
      setSelectedTimeSlots(slots)
    } else {
      // 範囲外の場合は新しい選択のみ
      setSelectedTimeSlots([timeString])
    }
  }

  // 部屋を選択する処理
  const handleRoomSelect = (roomId: number): void => {
    setSelectedRoom(roomId)
    // 部屋を変更した場合は選択した時間をリセット
    setSelectedTimeSlots([])
  }

  // 次へボタンの処理
  const handleNext = (): void => {
    if (!selectedRoom || selectedTimeSlots.length === 0 || !selectedDate) {
      return
    }
    
    // 選択された時間をソートして開始・終了時間を決定
    const sortedSlots = [...selectedTimeSlots].sort()
    const startTime = sortedSlots[0]
    const lastSlot = sortedSlots[sortedSlots.length - 1]
    
    // 終了時間は最後のスロット + 30分
    const [hourStr, minuteStr] = lastSlot.split(':')
    const hour = parseInt(hourStr, 10)
    const minute = parseInt(minuteStr, 10)
    const endDateTime = selectedDate.hour(hour).minute(minute).add(30, 'minute')
    const endTime = endDateTime.format('HH:mm')
    
    goToNext({
      roomId: selectedRoom,
      startTime,
      endTime
    })
  }

  // ボタンの境界線を計算する関数
  const getButtonBorderRadius = (roomName: string | undefined | null): string => {
    if (!roomName) return '0';
    if (roomName === 'A') {
      return '8px 0 0 8px';
    }
    if (roomName === 'E') {
      return '0 8px 8px 0';
    }
    return '0';
  };

  // 時間スロットのデータを生成（1列表示用）
  const timeSlots = useMemo(() => {
    const slots: TimeSlot[] = []
    
    // 9時から22時まで、30分刻み
    for (let hour = 9; hour <= 22; hour += 1) {
      for (const minute of [0, 30]) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        
        slots.push({
          hour,
          minute,
          timeString,
          available: selectedRoom ? !isTimeSlotReserved(selectedRoom, timeString) : true,
          selected: isTimeSlotSelected(timeString)
        })
      }
    }
    
    return slots
  }, [selectedRoom, isTimeSlotReserved, isTimeSlotSelected])

  if (roomListQuery.isLoading || roomReservationListQuery.isLoading) {
    return <AppLoading />
  }
  
  return (
    <Box
      pt={isMobile ? '0px' : '16px'}
      pb={isMobile ? '0px' : '16px'}
      backgroundColor="grand.white"
      height="100vh"
      overflow="hidden"
    >
      <Container maxW="container.md" height="100%">
        <Box mt={isMobile ? 8 : 2} height="100%" display="flex" flexDirection="column">
          <Flex alignItems="center" gap="8px" mb={isMobile ? 6 : 3}>
            <Icon as={MdMeetingRoom} boxSize={isMobile ? "20px" : "18px"} color="gray.700" />
            <Text fontSize={isMobile ? "20px" : "16px"} fontWeight="700">
              2.部屋を選んでください(部屋を切り替えて、利用できる時間を探せます)
            </Text>
          </Flex>
          
          {/* 部屋選択ボタン */}
          <Flex 
            mb={isMobile ? 6 : 3} 
            justifyContent="center" 
            flexWrap={isMobile ? "wrap" : "nowrap"}
            gap={isMobile ? "8px" : "0px"}
          >
            {(roomListQuery.data?.rooms ?? []).map((room) => (
              <Button
                key={room.id}
                aria-label={`部屋${getRoomShortName(room)}を選択`}
                size="lg"
                borderRadius={isMobile ? "8px" : getButtonBorderRadius(getRoomShortName(room))}
                borderWidth="2px"
                borderColor={PRIMARY_COLOR}
                backgroundColor={selectedRoom === room.id ? PRIMARY_COLOR : 'white'}
                color={selectedRoom === room.id ? 'white' : PRIMARY_COLOR}
                fontWeight="600"
                fontSize={isMobile ? "14px" : "16px"}
                mx={isMobile ? "0" : "0"}
                onClick={() => handleRoomSelect(room.id)}
                _hover={{ opacity: 0.8 }}
                height={isMobile ? "48px" : "50px"}
                minWidth={isMobile ? "80px" : "60px"}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                p={2}
                flexShrink={isMobile ? 1 : 0}
              >
                {getRoomShortName(room)}
              </Button>
            ))}
          </Flex>
          
          <Flex alignItems="center" gap="8px" mb={2}>
            <Icon as={MdAccessTime} boxSize={isMobile ? "20px" : "18px"} color="gray.700" />
            <Text fontSize={isMobile ? "20px" : "16px"} fontWeight="700">
              3.希望時間枠を選んでください（30分〜90分で選択可能。通話は終了時間が来ると自動的に終了します）
            </Text>
          </Flex>
          
          {selectedTimeSlots.length > 0 && (
            <Text fontSize={isMobile ? "16px" : "14px"} color="green.600" mb={2} fontWeight="600">
              選択中：{(() => {
                const sortedSlots = selectedTimeSlots.sort()
                if (sortedSlots.length === 1) {
                  return sortedSlots[0]
                } 
                  // 最後の枠の時間に30分を追加して終了時間を計算
                  const lastSlot = sortedSlots[sortedSlots.length - 1]
                  const [hour, minute] = lastSlot.split(':').map(Number)
                  const endTime = dayjs().hour(hour).minute(minute).add(30, 'minute').format('HH:mm')
                  return `${sortedSlots[0]}〜${endTime}`
                
              })()} ({selectedTimeSlots.length * 30}分間)
            </Text>
          )}
          
          {/* 時間選択テーブル（1列表示） */}
          <Box flex="1" mb={6} minHeight="200px">
            <TimeTable maxHeight={isMobile ? "calc(100vh - 450px)" : "calc(100vh - 520px)"}>
              {timeSlots.map((timeSlot) => (
                <Tr key={timeSlot.timeString} borderBottomWidth="1px" borderBottomColor="gray.200">
                  <Td
                    px={2}
                    py={2}
                    fontWeight="bold"
                    textAlign="center"
                    borderRightWidth="1px"
                    borderRightColor="gray.200"
                    fontSize={{ base: "16px", md: "20px" }}
                    bg="white"
                    width="100px"
                  >
                    {timeSlot.timeString}
                  </Td>
                  <Td p={2} bg="white">
                    <TimeSlotButton
                      isAvailable={timeSlot.available}
                      isActive={timeSlot.selected}
                      onClick={() => handleTimeSelect(timeSlot.timeString)}
                      aria-label={`${timeSlot.timeString}を選択`}
                    />
                  </Td>
                </Tr>
              ))}
            </TimeTable>
          </Box>
          
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button onClick={goBack} size="lg">戻る</Button>
            <Button
              colorScheme="green"
              onClick={handleNext}
              isDisabled={!selectedRoom || selectedTimeSlots.length === 0}
              size="lg"
            >
              次へ
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default ReservationCreateRoomTime 